'use client';

import { c as _c } from "react/compiler-runtime";
import { EVENT_DISPATCH_AMPLITUDE } from '@repo/analytics/analytics';
import { useAnalytics } from '@repo/analytics/analytics-provider';
import type { ModalProps } from '@repo/design-system/components/ui/modal';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectExternalMapId, selectTitleId, selectTitleName } from '@repo/store/maps/slices/mapData';
import type { MarkFoundPromptVariant } from '@repo/types/analytic-events/maps-analytic-events';
import type { WebLoginPromptTrigger } from '@repo/types/analytic-events/website-analytic-events';
import { HostType } from '@repo/types/host';
import authHeroBgMaps from '@repo/ui/assets/images/auth-modal/auth-hero-bg-maps.webp';
import { useAuth } from '@repo/ui-web/auth/auth-hooks';
import { AuthModal } from '@repo/ui-web/components/auth/user-login/auth-modal';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { MarkFoundDownloadModal } from './mark-found-download-modal';
import { getOrAssignMarkFoundPromptVariant, MARK_FOUND_PROMPT_EXPERIMENT, persistMarkFoundPromptVariant } from './mark-found-prompt-experiment';
interface MapsAuthModalProps extends ModalProps {
  trigger: WebLoginPromptTrigger;
}
function initializeMarkFoundPromptVariant(participatesInExperiment: boolean): MarkFoundPromptVariant | null {
  if (!participatesInExperiment || typeof document === 'undefined') return null;
  const variant = getOrAssignMarkFoundPromptVariant(document.cookie);
  persistMarkFoundPromptVariant(variant);
  return variant;
}
export const MapsAuthModal: React.FC<MapsAuthModalProps> = t0 => {
  const $ = _c(48);
  let isOpen;
  let onOpenChange;
  let props;
  let trigger;
  if ($[0] !== t0) {
    ({
      trigger,
      isOpen,
      onOpenChange,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = isOpen;
    $[2] = onOpenChange;
    $[3] = props;
    $[4] = trigger;
  } else {
    isOpen = $[1];
    onOpenChange = $[2];
    props = $[3];
    trigger = $[4];
  }
  const analytics = useAnalytics();
  const platform = useHostType();
  const {
    login,
    register,
    isAuthenticated
  } = useAuth();
  const mapId = useAppSelector(selectExternalMapId);
  const titleId = useAppSelector(selectTitleId);
  const titleName = useAppSelector(selectTitleName);
  const participatesInExperiment = trigger === "mark-found" && platform === HostType.WEB;
  let t1;
  if ($[5] !== participatesInExperiment) {
    t1 = () => initializeMarkFoundPromptVariant(participatesInExperiment);
    $[5] = participatesInExperiment;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const [experimentVariant, setExperimentVariant] = useState(t1);
  const exposureTrackedRef = useRef(false);
  let t2;
  let t3;
  if ($[7] !== experimentVariant || $[8] !== participatesInExperiment) {
    t2 = () => {
      if (!participatesInExperiment || experimentVariant) {
        return;
      }
      setExperimentVariant(initializeMarkFoundPromptVariant(true));
    };
    t3 = [experimentVariant, participatesInExperiment];
    $[7] = experimentVariant;
    $[8] = participatesInExperiment;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[11] !== analytics || $[12] !== experimentVariant || $[13] !== isOpen || $[14] !== mapId || $[15] !== platform || $[16] !== titleId || $[17] !== titleName) {
    t4 = () => {
      if (!isOpen) {
        exposureTrackedRef.current = false;
        return;
      }
      if (!experimentVariant || exposureTrackedRef.current) {
        return;
      }
      exposureTrackedRef.current = true;
      analytics.setUserProperty(MARK_FOUND_PROMPT_EXPERIMENT, experimentVariant, EVENT_DISPATCH_AMPLITUDE);
      analytics.event("game_map_mark_found_prompt_impression", {
        experiment: MARK_FOUND_PROMPT_EXPERIMENT,
        variant: experimentVariant,
        mapId: mapId ?? "",
        titleId: titleId ?? "",
        titleName: titleName ?? "",
        platform
      }, EVENT_DISPATCH_AMPLITUDE);
    };
    t5 = [analytics, experimentVariant, isOpen, mapId, platform, titleId, titleName];
    $[11] = analytics;
    $[12] = experimentVariant;
    $[13] = isOpen;
    $[14] = mapId;
    $[15] = platform;
    $[16] = titleId;
    $[17] = titleName;
    $[18] = t4;
    $[19] = t5;
  } else {
    t4 = $[18];
    t5 = $[19];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[20] !== isAuthenticated || $[21] !== isOpen || $[22] !== onOpenChange) {
    t6 = () => {
      if (isAuthenticated && isOpen) {
        onOpenChange?.({
          open: false
        });
      }
    };
    t7 = [isAuthenticated, isOpen, onOpenChange];
    $[20] = isAuthenticated;
    $[21] = isOpen;
    $[22] = onOpenChange;
    $[23] = t6;
    $[24] = t7;
  } else {
    t6 = $[23];
    t7 = $[24];
  }
  useEffect(t6, t7);
  if (participatesInExperiment && experimentVariant === "download") {
    let t8;
    if ($[25] !== analytics || $[26] !== experimentVariant || $[27] !== mapId || $[28] !== platform || $[29] !== titleId || $[30] !== titleName) {
      t8 = () => {
        analytics.event("game_map_mark_found_download_click", {
          experiment: MARK_FOUND_PROMPT_EXPERIMENT,
          variant: experimentVariant,
          mapId: mapId ?? "",
          titleId: titleId ?? "",
          titleName: titleName ?? "",
          platform
        }, EVENT_DISPATCH_AMPLITUDE);
      };
      $[25] = analytics;
      $[26] = experimentVariant;
      $[27] = mapId;
      $[28] = platform;
      $[29] = titleId;
      $[30] = titleName;
      $[31] = t8;
    } else {
      t8 = $[31];
    }
    let t9;
    if ($[32] !== isOpen || $[33] !== onOpenChange || $[34] !== props || $[35] !== t8) {
      t9 = <MarkFoundDownloadModal {...props} isOpen={isOpen} onOpenChange={onOpenChange} onDownloadClick={t8} />;
      $[32] = isOpen;
      $[33] = onOpenChange;
      $[34] = props;
      $[35] = t8;
      $[36] = t9;
    } else {
      t9 = $[36];
    }
    return t9;
  }
  if (participatesInExperiment && experimentVariant === null) {
    return null;
  }
  let t8;
  if ($[37] !== register) {
    t8 = async credentials => {
      await register(credentials);
    };
    $[37] = register;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  let t9;
  if ($[39] !== analytics || $[40] !== isOpen || $[41] !== login || $[42] !== onOpenChange || $[43] !== platform || $[44] !== props || $[45] !== t8 || $[46] !== trigger) {
    t9 = <AuthModal {...props} isOpen={isOpen} onOpenChange={onOpenChange} trigger={trigger} heroImageSrc={authHeroBgMaps.src} analytics={analytics} platform={platform} onLogin={login} onRegister={t8} />;
    $[39] = analytics;
    $[40] = isOpen;
    $[41] = login;
    $[42] = onOpenChange;
    $[43] = platform;
    $[44] = props;
    $[45] = t8;
    $[46] = trigger;
    $[47] = t9;
  } else {
    t9 = $[47];
  }
  return t9;
};